
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable no-console */
/* eslint-disable @typescript-eslint/no-unused-vars */

import { useTranslation } from 'react-i18next';

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { TextField, ThemeProvider,  MenuItem,  Button, Box, FormControlLabel, Checkbox, Link, Autocomplete, FormControl, FormHelperText, Select, InputLabel, Typography, InputAdornment } from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import Grid2 from '@mui/material/Grid2';
import { Formik, FormikErrors, FormikHelpers } from 'formik';
import { useAlert } from '../../alertProvider';
import { useLoader } from '../../loaderProvider/loader';
import { ConfirmationModal } from '../../confirmationModal';
import FormObserver from '../../FormikFormObserver';
import { getMaxDateForMonthInput, RequiredLabel } from '../../../utils/commonUtils';
import { usePatientContext } from '../../SubPatinetProvider';
import styles from '../oocyteRetrieval/oocyteRetrival.module.css';
import theme from '../../../utils/theme';

import {  getUsersByClinicId, uploadFileURL } from '../../../services/apiService';
import { useClinic } from '../../ClinicProvider';
import { addLaparoscopy,   ebHpeItems,  getLaparoscopy, initialLaparoscopy, LaparoscopyType, LaparoscopyValidationSchema,  positionItems, shapeItems, siteItems, updateLaparoscopy, deleteLaparoscopyAttachment } from './laparoscopy.helper';
import ImageUploadComponent from './imageUploadLaparoscopy';
import { Height } from '@mui/icons-material';
import { FileTypes } from '../../../types';
import { ImageUploadModal, UploadButton, UploadedImageType } from '../../common/ImageUploadModal';
import { MasterconfigrutationDetails } from '../oocyteRetrieval/OocyteRetrival.helper';


interface LaparoscopyFormProp {
  record: number;
  onFormChange?: (hasChanged: boolean) => void;
  onBack: () => void;
  isEditMode: boolean;
  dropDownOptions?: MasterconfigrutationDetails;
}


const Laparoscopy: React.FC<LaparoscopyFormProp> = ({ record, onFormChange, onBack, isEditMode = false, dropDownOptions }) => {
  const { t: tGeneral } = useTranslation('general');
  const { t: tSurgery } = useTranslation('surgery_config');
  const handleFormChange = onFormChange || (() => {});
  const [hasReset, setHasReset] = useState(false);
  const { patientId } = usePatientContext();
  const { addAlert } = useAlert();
  const { showLoader, hideLoader } = useLoader();
  const [initialValues, setInitialValues] = useState<LaparoscopyType>(initialLaparoscopy);
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const maxMonthDate = useMemo(() => getMaxDateForMonthInput(), []);
  const [openResetConfirmDialog, setOpenResetConfirmDialog] = useState(false);
  const [doctorList, setDoctorList] = useState<any[]>([]);
  const { ClinicId } = useClinic();
  const [file, setFile] = useState<File | null>(null);
  const [imagePathUrl, setImagePathUrl] = useState<string | null>(null);
  const [uploadedFiles, setUploadedFiles] = useState<UploadedImageType[]>([]);

  // Log the record prop to check data
  useEffect(() => {
    console.log('Laparoscopy record prop:', isEditMode);
    console.log('Laparoscopy record prop:', record);
  }, [isEditMode, record]);


  useEffect(() => {
    showLoader();
    getLaparoscopy(record)
      .then((data) => {
        setInitialValues({ ...initialLaparoscopy, ...data });

        if (data.image_path_url) {
          setImagePathUrl(data.image_path_url); // Set the Base64 image data
        }
        
        // Load existing images into uploadedFiles state for edit mode
        if (data.images && data.images.length > 0) {
          const existingFiles: UploadedImageType[] = data.images.map((img, index) => {
            // Use file_name from API if available, otherwise extract from URL
            let fileName = img.file_name || `Document_${index + 1}`;
            if (!img.file_name && img.image) {
              try {
                const urlPath = new URL(img.image).pathname;
                const extractedName = urlPath.split('/').pop();
                if (extractedName && extractedName.length > 0) {
                  fileName = decodeURIComponent(extractedName);
                }
              } catch {
                // Keep default name if URL parsing fails
              }
            }
            return {
              name: fileName,
              image: img.image || '',
              id: img.id,
              size: img.file_size ? (img.file_size / (1024 * 1024)).toFixed(2) : undefined,
              sizeBytes: img.file_size,
              file_extension: img.file_extension,
              isExisting: true,
            };
          });
          setUploadedFiles(existingFiles);
        }
      })
       
      .catch(() => console.error('Error fetching Laparoscopy', 'error'))
      .finally(() => hideLoader());
  }, [addAlert, showLoader, hideLoader, record]);

  
  const sanitizeLaparoscopy = (values: LaparoscopyType): LaparoscopyType => {
    const sanitizedValues = { ...values, patient_procedure: record };
    
    if (uploadedFiles.length > 0) {
      // Process files - include file metadata for backend
      const sanitizedImages = uploadedFiles.map(file => {
        // For existing files, just pass the id
        if (file.isExisting && file.id) {
          return { id: file.id };
        }
        // For new files, include base64 data and metadata
        const cleanedImage = file.image.replace(/^data:(image\/\w+|application\/pdf);base64,/, '');
        return {
          image: cleanedImage,
          file_name: file.name,
          file_size: file.sizeBytes,
          file_extension: file.file_extension,
        };
      });
      
      // Assign to the images field
      sanitizedValues.images = sanitizedImages;
    }

    return sanitizedValues;
  };
  
  const handleSubmit = async (
    values: LaparoscopyType,
    { setSubmitting, setErrors, resetForm }: FormikHelpers<LaparoscopyType>
  ) => {
    showLoader();

    // Use the utility function to sanitize values
    // const sanitizedValues = sanitizeLaparoscopy(values);
    // addLaparoscopy(sanitizedValues)
    // Use the utility function to sanitize values
    const sanitizedValues = sanitizeLaparoscopy(values);
    let laparoscopyApiCall; // Renamed for clarity
    if (isEditMode) {
      // Ensure that sanitizedValues.id is a valid number
      const id = sanitizedValues?.id;
    
      if (id !== undefined && id !== null) { // Check if ID is not undefined or null
        laparoscopyApiCall = updateLaparoscopy(id, sanitizedValues);
      } else {
        console.error('ID is required for updating laparoscopy');
        hideLoader();
        setSubmitting(false);
        return; // Exit early if ID is not valid
      }
    } else {
      // Otherwise, call the add API
      laparoscopyApiCall = addLaparoscopy(sanitizedValues);
    }

    laparoscopyApiCall
      .then((updatedData) => {

        if (file) {
          const fileName = file.name ; 
          if (updatedData.id !== undefined && updatedData.id !== null) {
            uploadFileURL({ file_name: fileName, source: FileTypes.LAPAROSCOPY, ref_id: updatedData.id }, file as File);
          }
        }
        addAlert(tGeneral('laparoscopySavedSuccessfully'), 'success');
        setInitialValues(updatedData);
        resetForm({ values: updatedData });
        onBack();

      })  
      .catch((error) => {
        if (error.response && error.response.data && error.response.data.type === 'validation_error') {
          const apiErrors = error.response.data.errors;
          const formikErrors: FormikErrors<LaparoscopyType> = {};

          apiErrors.forEach((error: { attr: keyof LaparoscopyType; detail: string }) => {
            formikErrors[error.attr] = error.detail;
          });

          setErrors(formikErrors);
        } else {
           
          console.error('An error occurred while saving Laparoscopy.');
        }
      })
      .finally(() => {
        hideLoader();
        setSubmitting(false);
      });
  };
  
  const handleReset = (
    resetForm: FormikHelpers<LaparoscopyType>['resetForm'],
    setFieldValue: FormikHelpers<LaparoscopyType>['setFieldValue']
  ) => {
    setOpenResetConfirmDialog(false); 
    resetForm(); 
    setHasReset(true); 
    
    Object.keys(initialLaparoscopy).forEach((field) => {
      const initialValue = initialLaparoscopy[field as keyof LaparoscopyType];
      if (typeof initialValue === 'number' || initialValue === null) {
        setFieldValue(field, '');
      } else {
        setFieldValue(field, initialValue);
      }
    });
  };
   
  useEffect(() => {
    showLoader();
    const query = `clinic=${ClinicId}&role=DOCTOR`;
    getUsersByClinicId(query)
      .then((res: any) => {
        setDoctorList(res?.objects ?? []);
      })
      .catch((error) => {
        console.error('Error fetching clinicians:', error);
      })
      .finally(() => {
        hideLoader();
      });
  }, [ClinicId, showLoader, hideLoader]);


  const [isImageUploadModalOpen, setIsImageUploadModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);

  const handleOpenImageUploadModal = () => setIsImageUploadModalOpen(true);
  const handleCloseImageUploadModal = () => setIsImageUploadModalOpen(false);
  const handleOpenViewModal = () => setIsViewModalOpen(true);
  const handleCloseViewModal = () => setIsViewModalOpen(false);

  const handleSaveUploadedImages = (images: UploadedImageType[]) => {
    setUploadedFiles(images);
  };

  // Handler for deleting an image via the API (for existing images)
  const deleteImage = async (imageId: number): Promise<void> => {
    showLoader();
    try {
      await deleteLaparoscopyAttachment(imageId);
      addAlert(tGeneral('imageDeletedSuccessfully') || 'Image deleted successfully', 'success');
    } catch (err) {
      console.error('Error deleting image:', err);
      addAlert(tGeneral('failedToDeleteImage') || 'Failed to delete image', 'error');
      throw err; // Re-throw so ImageUploadModal knows deletion failed
    } finally {
      hideLoader();
    }
  };

  
  return (
    <Formik 
      initialValues={initialValues}
      validationSchema={LaparoscopyValidationSchema}
      onSubmit={handleSubmit}
      enableReinitialize
    >      
      {({         
        values,
        setFieldValue,
        submitForm,
        validateForm,
        errors,
        setFieldError, 
        dirty,  handleBlur, handleChange, handleSubmit, isSubmitting, touched, resetForm, setFieldTouched }) => (
        <>        
          <FormObserver dirty={dirty} onFormChange={handleFormChange} />          
          <ThemeProvider theme={theme}>
            <form onSubmit={handleSubmit}>
              <Grid2 container spacing={2} justifyContent="start" sx={{ marginTop: 2 , marginRight:2}}>
                <Grid2 size={{ xs: 6, sm:4, md: 4, lg:3 }}>
                  <TextField
                    fullWidth
                    size="small"
                    label={RequiredLabel(tGeneral('date'), true)}
                    name="date"
                    type="date"
                    InputLabelProps={{ shrink: true }}
                    value={values.date}
                    onBlur={handleBlur}
                    onChange={(e) => setFieldValue('date', e.target.value || null)}
                    error={touched.date && Boolean(errors.date)}
                    inputProps={{ max: maxMonthDate }}
                    aria-label="Date Input Field"
                  />
                </Grid2>

                <Grid2 size={{ xs: 6, sm: 4, md: 4, lg: 3 }}>
                  <TextField
                    fullWidth
                    size="small"
                    label={RequiredLabel(tSurgery('indication'), true)}
                    name="indication"
                    value={values.indication || ''}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    error={touched.indication && Boolean(errors.indication)}
                  />
                </Grid2>

                <Grid2 size={{ xs: 6, sm: 4, md: 4, lg: 3 }}>
                  <TextField
                    fullWidth
                    size="small"
                    label={RequiredLabel(tSurgery('cycleDay'), true)}
                    name="cycle_day"
                    value={values.cycle_day || ''}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    error={touched.cycle_day && Boolean(errors.cycle_day)}
                  />
                </Grid2>

                <Grid2 size={{ xs: 6, sm: 4, md: 4, lg: 3 }}>
                  <TextField
                    fullWidth
                    size="small"
                    label={RequiredLabel(tSurgery('procedureDuration'), false)}
                    name="procedure_duration"
                    type="number"
                    value={values.procedure_duration || ''}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    error={touched.procedure_duration && Boolean(errors.procedure_duration)}
                    helperText={touched.procedure_duration && errors.procedure_duration}
                    placeholder="0"
                    slotProps={{
                      htmlInput: { min: 0, max: 720, step: 5, inputMode: 'numeric' },
                      input: {
                        endAdornment: (
                          <InputAdornment position="end">
                            <AccessTimeIcon sx={{ fontSize: 18, color: 'text.secondary', mr: 0.5 }} />
                            <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                              {tSurgery('durationUnitMins')}
                            </Typography>
                          </InputAdornment>
                        ),
                      },
                    }}
                  />
                </Grid2>
              </Grid2>

              <Typography
                sx={{ fontFamily: 'Montserrat', fontSize: '16px', fontWeight: 600, lineHeight: '19.5px', textAlign: 'left', marginTop: 4, marginBottom: 2 }}
              >
                {tSurgery('anesthesiaDetails')}
              </Typography>


              <Grid2 container spacing={2} justifyContent="start" sx={{ marginY: 2, marginRight:2 }}>
                {/* Anesthesia Type Field */}
                <Grid2 size={{ xs: 6, sm: 4, md: 4, lg: 3 }}>
                  <TextField
                    fullWidth
                    size="small"
                    select
                    label={tSurgery('anesthesiaType')}
                    name="anesthesia_type"
                    value={values.anesthesia_type || ''}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    error={touched.anesthesia_type && Boolean(errors.anesthesia_type)}
                    // helperText={touched.anesthesia_type && errors.anesthesia_type}
                  >
                    {/* Dynamically generating options from the anesthesiaItems array */}
                    {dropDownOptions?.Laparo_anesthesia_type.map((item) => (
                      <MenuItem key={item.value} value={item.value}>
                        {item.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid2>

                {/* Anesthesia Details Remark Field */}
                <Grid2 size={{ xs: 6, sm: 4, md: 4, lg: 3 }}>
                  <TextField
                    fullWidth
                    size="small"
                    label={tSurgery('anesthesiaDetailsRemark')}
                    name="anesthesia_details_remark"
                    value={values.anesthesia_details_remark || ''}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    error={touched.anesthesia_details_remark && Boolean(errors.anesthesia_details_remark)}
                    helperText={touched.anesthesia_details_remark && errors.anesthesia_details_remark}
                    multiline
                    minRows={1}
                    maxRows={1}
                  />
                </Grid2>

                {/* Preanesthetic Check Up Field */}
                <Grid2 size={{ xs: 6, sm: 4, md: 4, lg: 3 }}>
                  <TextField
                    fullWidth
                    size="small"
                    label={tSurgery('preanestheticCheckup')}
                    name="preanesthetic_checkup"
                    value={values.preanesthetic_checkup || ''}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    error={touched.preanesthetic_checkup && Boolean(errors.preanesthetic_checkup)}
                    helperText={touched.preanesthetic_checkup && errors.preanesthetic_checkup}
                    multiline
                    minRows={1}
                    maxRows={1}
                  />
                </Grid2>
              </Grid2>


              {/* Patency of Fallopian Tube Section */}
              <Typography
                sx={{ fontFamily: 'Montserrat', fontSize: '16px', fontWeight: 600, lineHeight: '19.5px', textAlign: 'left', marginTop: 4, marginBottom: 2 }}
              >
                {tSurgery('patencyOfFallopianTube')}
              </Typography>

              <Grid2 container spacing={2} justifyContent="start" sx={{ marginY: 2, marginRight:2 }}>
                {/* Right Tube Field */}
                <Grid2 size={{ xs: 6, sm: 4, md: 4, lg: 3 }}>
                  <TextField
                    fullWidth
                    size="small"
                    label={tSurgery('rightTube')}
                    name="right_tube_status"
                    value={values.right_tube_status || ''}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    error={touched.right_tube_status && Boolean(errors.right_tube_status)}
                    // helperText={touched.right_tube_status && errors.right_tube_status}
                  />
                </Grid2>

                {/* Left Tube Field */}
                <Grid2 size={{ xs: 6, sm: 4, md: 4, lg: 3 }}>
                  <TextField
                    fullWidth
                    size="small"
                    label={tSurgery('leftTube')}
                    name="left_tube_status"
                    value={values.left_tube_status || ''}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    error={touched.left_tube_status && Boolean(errors.left_tube_status)}
                    // helperText={touched.left_tube_status && errors.left_tube_status}
                  />
                </Grid2>
              </Grid2>

              {/* Right Ovary Section */}
              <Typography
                sx={{ fontFamily: 'Montserrat', fontSize: '16px', fontWeight: 600, lineHeight: '19.5px', textAlign: 'left', marginTop: 4, marginBottom: 2 }}
              >
                {tSurgery('rightOvary')}
              </Typography>

              <Grid2 container spacing={2} justifyContent="start" sx={{ marginY: 2, marginRight:2 }}>
                {/* Right Ovary Status Field */}
                <Grid2 size={{ xs: 6, sm: 4, md: 4, lg: 3 }}>
                  <TextField
                    fullWidth
                    size="small"
                    label={tSurgery('status')}
                    name="right_ovary_status"
                    value={values.right_ovary_status || ''}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    error={touched.right_ovary_status && Boolean(errors.right_ovary_status)}
                    // helperText={touched.right_ovary_status && errors.right_ovary_status}
                  />
                </Grid2>

                {/* Right Ovary Other Details Field */}
                <Grid2 size={{ xs: 6, sm: 4, md: 4, lg: 3 }}>
                  <TextField
                    fullWidth
                    size="small"
                    label={tSurgery('details')}
                    name="right_ovary_details"
                    value={values.right_ovary_details || ''}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    error={touched.right_ovary_details && Boolean(errors.right_ovary_details)}
                    // helperText={touched.right_ovary_details && errors.right_ovary_details}
                    multiline
                    minRows={1}
                    maxRows={1}
                  />
                </Grid2>
              </Grid2>

              {/* Left Ovary Section */}
              <Typography
                sx={{ fontFamily: 'Montserrat', fontSize: '16px', fontWeight: 600, lineHeight: '19.5px', textAlign: 'left', marginTop: 4, marginBottom: 2 }}
              >
                {tSurgery('leftOvary')}
              </Typography>

              <Grid2 container spacing={2} justifyContent="start" sx={{ marginY: 2, marginRight:2 }}>
                {/* Left Ovary Status Field */}
                <Grid2 size={{ xs: 6, sm: 4, md: 4, lg: 3 }}>
                  <TextField
                    fullWidth
                    size="small"
                    label={tSurgery('status')}
                    name="left_ovary_status"
                    value={values.left_ovary_status || ''}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    error={touched.left_ovary_status && Boolean(errors.left_ovary_status)}
                    // helperText={touched.left_ovary_status && errors.left_ovary_status}
                  />
                </Grid2>

                {/* Left Ovary Other Details Field */}
                <Grid2 size={{ xs: 6, sm: 4, md: 4, lg: 3 }}>
                  <TextField
                    fullWidth
                    size="small"
                    label={tSurgery('otherDetails')}
                    name="left_ovary_details"
                    value={values.left_ovary_details || ''}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    error={touched.left_ovary_details && Boolean(errors.left_ovary_details)}
                    // helperText={touched.left_ovary_details && errors.left_ovary_details}
                    multiline
                    minRows={1}
                    maxRows={1}
                  />
                </Grid2>
              </Grid2>

              <Typography
                sx={{ fontFamily: 'Montserrat', fontSize: '16px', fontWeight: 600, lineHeight: '19.5px', textAlign: 'left', marginTop: 4, marginBottom: 2 }}
              >
                {tSurgery('otherDetailsSection')}
              </Typography>

              <Grid2 container spacing={2} justifyContent="start" sx={{ marginY: 2, marginRight:2 }}>
                {/* Site Field */}
                <Grid2 size={{ xs: 6, sm: 4, md: 4, lg: 3 }}>
                  <TextField
                    fullWidth
                    size="small"
                    select
                    label={tSurgery('site')}
                    name="adhesion_site"
                    value={values.adhesion_site || ''}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    error={touched.adhesion_site && Boolean(errors.adhesion_site)}
                    // helperText={touched.adhesion_site && errors.adhesion_site}
                  >
                    {siteItems.map((item) => (
                      <MenuItem key={item.value} value={item.value}>
                        {item.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid2>

                {/* Adhesion Field */}
                <Grid2 size={{ xs: 6, sm: 4, md: 4, lg: 3 }}>
                  <TextField
                    fullWidth
                    size="small"
                    select
                    label={tSurgery('adhesion')}
                    name="adhesion_grade"
                    value={values.adhesion_grade || ''}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    error={touched.adhesion_grade && Boolean(errors.adhesion_grade)}
                    // helperText={touched.adhesion_grade && errors.adhesion_grade}
                  >
                    {dropDownOptions?.adhesion_grade.map((item) => (
                      <MenuItem key={item.value} value={item.value}>
                        {item.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid2>
                
                <Grid2 size={{ xs: 6, sm: 4, md: 4, lg: 3 }}>
                  <TextField
                    fullWidth
                    size="small"
                    label={tGeneral('remark')}
                    name="remark"
                    value={values.remark || ''}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    error={touched.remark && Boolean(errors.remark)}
                    helperText={touched.remark && errors.remark}
                    multiline
                    minRows={1}
                    maxRows={1}
                  />
                </Grid2>
              </Grid2>

              <Grid2 container spacing={2} justifyContent="start" sx={{ marginY: 2, marginRight:2 }}>                
                {/* Field for Endometriosis Grade */}
                <Grid2 size={{ xs: 6, sm: 4, md: 4, lg: 3 }}>
                  <TextField
                    fullWidth
                    size="small"
                    select
                    label={tSurgery('endometriosisGrade')}
                    name="endometriosis_grade"
                    value={values.endometriosis_grade || ''}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    error={touched.endometriosis_grade && Boolean(errors.endometriosis_grade)}
                    // helperText={touched.endometriosis_grade && errors.endometriosis_grade}
                  >
                    {dropDownOptions?.endometriosis_grade.map((item) => (
                      <MenuItem key={item.value} value={item.value}>
                        {item.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid2>

                <Grid2 size={{ xs: 6, sm: 4, md: 4, lg: 3 }}>
                  <TextField
                    fullWidth
                    size="small"
                    select
                    label={tSurgery('uterusType')}
                    name="uterus_status"
                    value={values.uterus_status || ''}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    error={touched.uterus_status && Boolean(errors.uterus_status)}
                    // helperText={touched.uterus_status && errors.uterus_status}
                  >
                    {dropDownOptions?.uterus_status.map((item) => (
                      <MenuItem key={item.value} value={item.value}>
                        {item.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid2>

                {/* Uterus Size Field */}
                <Grid2 size={{ xs: 6, sm: 4, md: 4, lg: 3 }}>
                  <TextField
                    fullWidth
                    size="small"
                    select
                    label={tSurgery('size')}
                    name="uterus_size"
                    value={values.uterus_size || ''}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    error={touched.uterus_size && Boolean(errors.uterus_size)}
                    // helperText={touched.uterus_size && errors.uterus_size}
                  >
                    {dropDownOptions?.uterus_size.map((item) => (
                      <MenuItem key={item.value} value={item.value}>
                        {item.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid2>

                {/* Uterus Shape Field */}
                <Grid2 size={{ xs: 6, sm: 4, md: 4, lg: 3 }}>
                  <TextField
                    fullWidth
                    size="small"
                    select
                    label={tSurgery('shape')}
                    name="uterus_shape"
                    value={values.uterus_shape || ''}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    error={touched.uterus_shape && Boolean(errors.uterus_shape)}
                    // helperText={touched.uterus_shape && errors.uterus_shape}
                  >
                    {shapeItems.map((item) => (
                      <MenuItem key={item.value} value={item.value}>
                        {item.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid2>

                {/* Additional fields for Position */}
                <Grid2 size={{ xs: 6, sm: 4, md: 4, lg: 3 }}>
                  <TextField
                    fullWidth
                    size="small"
                    select
                    label={tSurgery('position')}
                    name="uterus_position"
                    value={values.uterus_position || ''}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    error={touched.uterus_position && Boolean(errors.uterus_position)}
                    // helperText={touched.uterus_position && errors.uterus_position}
                  >
                    {positionItems.map((item) => (
                      <MenuItem key={item.value} value={item.value}>
                        {item.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid2>

                {/* Additional fields for Mobility */}
                <Grid2 size={{ xs: 6, sm: 4, md: 4, lg: 3 }}>
                  <TextField
                    fullWidth
                    size="small"
                    select
                    label={tSurgery('mobility')}
                    name="uterus_mobility"
                    value={values.uterus_mobility || ''}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    error={touched.uterus_mobility && Boolean(errors.uterus_mobility)}
                    // helperText={touched.uterus_mobility && errors.uterus_mobility}
                  >
                    {dropDownOptions?.uterus_mobility.map((item) => (
                      <MenuItem key={item.value} value={item.value}>
                        {item.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid2>

                {/* Additional fields for Associated Procedure */}
                <Grid2 size={{ xs: 6, sm: 4, md: 4, lg: 3 }}>
                  <TextField
                    fullWidth
                    size="small"
                    select
                    label={tSurgery('associatedProcedure')}
                    name="associated_procedures"
                    value={values.associated_procedures || ''}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    error={touched.associated_procedures && Boolean(errors.associated_procedures)}
                    // helperText={touched.associated_procedures && errors.associated_procedures}
                  >
                    {dropDownOptions?.associated_procedures.map((item) => (
                      <MenuItem key={item.value} value={item.value}>
                        {item.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid2>


                <Grid2 size={{ xs: 6, sm: 4, md: 4, lg: 3 }}>
                  <TextField
                    fullWidth
                    size="small"
                    label={tGeneral('remark')}
                    name="uterus_remark"
                    value={values.uterus_remark || ''}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    error={touched.uterus_remark && Boolean(errors.uterus_remark)}
                    // helperText={touched.uterus_remark && errors.uterus_remark}
                    multiline
                    minRows={1}
                    maxRows={1}
                  />
                </Grid2>

                {/* Field for Pouch of Douglas */}
                <Grid2 size={{ xs: 6, sm: 4, md: 4, lg: 3 }}>
                  <TextField
                    fullWidth
                    size="small"
                    label={tSurgery('pouchOfDouglas')}
                    name="pouch_of_douglas_remark"
                    value={values.pouch_of_douglas_remark || ''}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    error={touched.pouch_of_douglas_remark && Boolean(errors.pouch_of_douglas_remark)}
                    // helperText={touched.pouch_of_douglas_remark && errors.pouch_of_douglas_remark}
                  />
                </Grid2>

                {/* Field for Liver Undersurface */}
                <Grid2 size={{ xs: 6, sm: 4, md: 4, lg: 3 }}>
                  <TextField
                    fullWidth
                    size="small"
                    select
                    label={tSurgery('liverUndersurface')}
                    name="undersurface_of_liver"
                    value={values.undersurface_of_liver || ''}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    error={touched.undersurface_of_liver && Boolean(errors.undersurface_of_liver)}
                    // helperText={touched.undersurface_of_liver && errors.undersurface_of_liver}
                  >
                    {dropDownOptions?.liver_undersurface.map((item) => (
                      <MenuItem key={item.value} value={item.value}>
                        {item.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid2>

                {/* Field for EB HPE */}
                <Grid2 size={{ xs: 6, sm: 4, md: 4, lg: 3 }}>
                  <TextField
                    fullWidth
                    size="small"
                    select
                    label={tSurgery('ebHpe')}
                    name="eb_hpe"
                    value={values.eb_hpe || ''}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    error={touched.eb_hpe && Boolean(errors.eb_hpe)}
                    // helperText={touched.eb_hpe && errors.eb_hpe}
                  >
                    {ebHpeItems.map((item) => (
                      <MenuItem key={item.value} value={item.value}>
                        {item.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid2>

                
                <Grid2 size={{ xs: 6, sm: 4, md: 4, lg: 3 }}>
                  <TextField
                    fullWidth
                    size="small"
                    label={tGeneral('remark')}
                    name="eb_hpe_remark"
                    value={values.eb_hpe_remark || ''}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    error={touched.eb_hpe_remark && Boolean(errors.eb_hpe_remark)}
                    // helperText={touched.eb_hpe_remark && errors.eb_hpe_remark}
                    multiline
                    minRows={1}
                    maxRows={1}
                  />
                </Grid2>

                {/* Field for Done By */}
                <Grid2 size={{ xs: 6, sm: 4, md: 4, lg: 3 }}>
                  <Autocomplete
                    fullWidth
                    size="small"
                    id="done_by"
                    options={doctorList}
                    getOptionLabel={(option) => (option.first_name || '') + ' ' + (option.last_name || '')}
                    value={doctorList.find((doctor) => doctor.id === values.done_by) || null}
                    onChange={(_, value) => {
                      setFieldValue('done_by', value?.id || '');
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={tSurgery('doneBy')}
                        error={touched.done_by && Boolean(errors.done_by)}
                        // helperText={touched.done_by && errors.done_by}
                      />
                    )}
                  />
                </Grid2>

                {/* Field for Associated Doctor */}
                <Grid2 size={{ xs: 6, sm: 4, md: 4, lg: 3 }}>
                  <Autocomplete
                    fullWidth
                    size="small"
                    id="associated_doctor"
                    options={doctorList} // Updated to use doctorList
                    getOptionLabel={(option) => (option.first_name || '') + ' ' + (option.last_name || '')}
                    value={doctorList.find((doctor) => doctor.id === values.associated_doctor) || null}
                    onChange={(_, value) => {
                      setFieldValue('associated_doctor', value?.id || '');
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={RequiredLabel(tSurgery('associatedDoctor'), true)}
                        error={touched.associated_doctor && Boolean(errors.associated_doctor)}
                        // helperText={touched.associated_doctor && errors.associated_doctor}
                      />
                    )}
                  />
                </Grid2>

                {/* Upload Button */}
                <Grid2 size={{ xs: 6, sm: 4, md: 4, lg: 3 }} sx={{ display: 'flex', alignItems: 'center' }}>
                  <UploadButton
                    label={tGeneral('upload')}
                    onClick={handleOpenImageUploadModal}
                    onViewClick={handleOpenViewModal}
                    fileCount={uploadedFiles.length}
                    variant="outlined"
                    size="small"
                    height={40}
                  />
                </Grid2>

                {/* Upload Modal */}
                <ImageUploadModal
                  open={isImageUploadModalOpen}
                  onClose={handleCloseImageUploadModal}
                  onSave={handleSaveUploadedImages}
                  uploadedImages={uploadedFiles}
                  onRemove={(images) => setUploadedFiles(images)}
                  onDeleteImage={(imageId: number) => deleteImage(imageId)}
                  maxFileSizeMB={20}
                />

                {/* View Modal - for previewing uploaded files */}
                <ImageUploadModal
                  open={isViewModalOpen}
                  onClose={handleCloseViewModal}
                  onSave={handleCloseViewModal}
                  uploadedImages={uploadedFiles}
                  title={tSurgery('viewDocuments')}
                  viewMode={true}
                />

              </Grid2>

              <Box sx={{flexGrow: 1, marginRight: 2}}>              
                <Box className={styles.customButtonDiv}>
                  
                  <button className={styles.resetButton} onClick={onBack} >{tGeneral('backToList')}</button>
                  <button
                    type="button"
                    className={styles.resetButton}
                    onClick={() => setOpenResetConfirmDialog(true)} 
                  >
                    {tGeneral('reset')}
                  </button>
                  <button
                    type="button"
                    className={`${styles.customButton} ${isSubmitting || !dirty ? styles.disabled : ''}`}
                    disabled={isSubmitting || !dirty} 
                    onClick={() => {
                      validateForm().then((errors) => {
                        if (Object.keys(errors).length === 0) {
                          // submitForm();
                          setOpenConfirmDialog(true);
                        } else {
                          const errorKeys = Object.keys(errors);                          
                          errorKeys.forEach((key) =>{
                            setFieldTouched(key, true, true);
                          });
                        }
                      });
                    }}                  
                  >                    
                    <span style={{ fontWeight: 'bold' }}>
                      {isSubmitting ? tGeneral('saving') : tGeneral('save')}
                    </span>
                  </button>                  
                </Box>

                <ConfirmationModal
                  open={openConfirmDialog}
                  handleClose={() => setOpenConfirmDialog(false)}
                  onConfirm={() => {
                    submitForm();
                    setOpenConfirmDialog(false);
                  }}
                  type="save"
                  title={tSurgery('laparoscopy')}
                  contentMessage={tSurgery('confirmSaveLaparoscopy')}
                />
                <ConfirmationModal
                  open={openResetConfirmDialog}
                  handleClose={() => setOpenResetConfirmDialog(false)} 
                  onConfirm={() => handleReset(resetForm, setFieldValue)} 
                  title={tSurgery('laparoscopy')}
                  contentMessage={tSurgery('confirmResetLaparoscopy')}
                  type={'delete'}
                />
              </Box>
            </form>
          </ThemeProvider>
        </>
      )}
    </Formik>
  );
};

export default Laparoscopy;